import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GithubLogoIcon, SpinnerGapIcon, StarIcon, ArrowSquareOutIcon, ChartLineUpIcon, WrenchIcon } from "@phosphor-icons/react";
import { fetchGithubRadar } from "../lib/api";
import { QuickBuyLink } from "../components/TradeButton";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const GREEN = "#00E599";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

const VERDICTS = {
  building: { label: "🔧 DEV IS BUILDING", color: "#00E599", note: "Pushed code recently" },
  active: { label: "🔧 ACTIVE REPO", color: "#7FD4FF", note: "Recent activity" },
  stale: { label: "💤 STALE REPO", color: "#F5A623", note: "No commits in a while" },
  missing: { label: "⚠️ FAKE / DEAD LINK", color: "#FF3366", note: "Repo not found" },
  unknown: { label: "❔ UNKNOWN", color: "#8A8F98", note: "Couldn't verify" },
};

const RepoRow = ({ r, rank }) => {
  const v = VERDICTS[r.verdict] || VERDICTS.unknown;
  return (
    <div
      data-testid={`github-row-${r.mint}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: `${v.color}33`, background: r.verdict === "building" ? `linear-gradient(90deg, ${v.color}14, transparent)` : "transparent" }}
    >
      <div className="w-6 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? v.color : "#666" }}>{rank}</div>
      {r.image ? (
        <img src={r.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${v.color}55`, color: v.color }}><GithubLogoIcon size={18} /></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${r.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.symbol || short(r.mint)}</Link>
          <span data-testid={`github-verdict-${r.mint}`} className="font-mono text-[9px] px-1.5 py-0.5 font-bold" style={{ background: `${v.color}18`, border: `1px solid ${v.color}`, color: v.color }}>{v.label}</span>
          {r.score != null && <span className="font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: "#4ADE8014", border: "1px solid #4ADE8055", color: GREEN }}>{r.score}/100</span>}
        </div>
        <a href={r.github} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-muted-foreground hover:text-neon flex items-center gap-1.5 mt-0.5 truncate">
          {r.owner}/{r.repo} <ArrowSquareOutIcon size={10} />
          {r.exists && (
            <span className="text-muted-foreground/80">
              · <StarIcon size={10} className="inline mb-0.5" />{r.stars ?? 0}
              {r.daysSincePush != null && ` · pushed ${r.daysSincePush}d ago`}
              {r.language && ` · ${r.language}`}
            </span>
          )}
        </a>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm font-bold">{fmtUsd(r.marketCap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <QuickBuyLink coin={{ address: r.mint }} testid={`github-buy-${r.mint}`} />
        <Link to={`/coin/${r.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`github-chart-${r.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

export default function GithubRadar() {
  useSeo({
    title: "🔧 GitHub Radar — Is the Dev Actually Building? | AlphaApeing",
    description: "For any token with a linked GitHub, GitHub Radar checks whether the repo is real and active — a legitimacy signal no other meme terminal has.",
  });

  const { data, isLoading } = useQuery({ queryKey: ["githubRadar"], queryFn: fetchGithubRadar, refetchInterval: 120000 });
  const repos = data?.repos || [];
  const building = repos.filter((r) => r.verdict === "building" || r.verdict === "active");
  const rest = repos.filter((r) => r.verdict !== "building" && r.verdict !== "active");

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="github-radar-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: GREEN }}><GithubLogoIcon size={24} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">GITHUB <span style={{ color: GREEN }}>RADAR</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Is the dev <span style={{ color: GREEN }} className="font-semibold">actually building</span>, or is the "GitHub" a dead link? For every trending token with a
          linked repo, we check if it's real and recently pushed. A live, active repo is <span className="text-white">rare</span> in memecoins —
          a strong legitimacy signal (not a buy signal). Turn on the Telegram "Dev is building" alert on the <Link to="/alerts" className="text-neon hover:underline">Alerts</Link> page.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: GREEN }} /></div>
      ) : repos.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <WrenchIcon size={28} style={{ color: GREEN }} />
          No trending tokens have a linked GitHub right now. That's normal — real repos are rare. Check back as the Radar refreshes.
        </div>
      ) : (
        <>
          {building.length > 0 && (
            <div className="space-y-3" data-testid="github-building-section">
              <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2"><span>🔧</span> Devs shipping code <span className="font-mono text-xs text-muted-foreground">{building.length}</span></h2>
              <div className="border divide-y divide-white/5" style={{ borderColor: `${GREEN}33` }}>
                {building.map((r, i) => <RepoRow key={r.mint} r={r} rank={i + 1} />)}
              </div>
            </div>
          )}
          {rest.length > 0 && (
            <div className="space-y-3" data-testid="github-rest-section">
              <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2"><span>🔍</span> Linked but unproven <span className="font-mono text-xs text-muted-foreground">{rest.length}</span></h2>
              <div className="border border-white/5 divide-y divide-white/5">
                {rest.map((r, i) => <RepoRow key={r.mint} r={r} rank={i + 1} />)}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <GithubLogoIcon size={13} style={{ color: GREEN }} />
        Repo data cached ~12h. "Building" = pushed within 14 days. A repo can be real and the token still rug — always DYOR.
      </div>
    </div>
  );
}
